import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    Typography,
    Divider,
    useTheme,
    styled,
    Collapse,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import DashboardIcons from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import ImageIcon from '@mui/icons-material/Image';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import HistoryIcon from '@mui/icons-material/History';
import FeedbackIcon from '@mui/icons-material/Feedback';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BoltIcon from '@mui/icons-material/Bolt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ForumIcon from '@mui/icons-material/Forum';
import CampaignIcon from '@mui/icons-material/Campaign';
import AbcIcon from '@mui/icons-material/Abc';
import BugReportIcon from '@mui/icons-material/BugReport';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Payment from '@mui/icons-material/Payment';
// --- NEW ---
// Import the hook to use the global chatbot context
import { useChatbots } from '../contexts/ChatbotContext'; 
import UpgradePlan from './UpgradePlan';


const SidebarHeaderButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(2, 2, 1, 2),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const SidebarHeaderText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active, disabled }) => ({
    '&:hover': {
        backgroundColor: !disabled ? theme.palette.action.hover : 'transparent',
    },
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    margin: theme.spacing(0.5, 1),
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    ...(active && !disabled && {
        backgroundColor: theme.palette.primary.light,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
            color: theme.palette.primary.contrastText,
        },
    }),
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme, active }) => ({
    color: active ? theme.palette.primary.contrastText : theme.palette.primary.main,
    minWidth: 40,
}));

const StyledListItemText = styled(ListItemText)(({ theme, active }) => ({
    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
}));

// The component props have been simplified as isOpen and onClose are more standard
export default function Sidebar({ drawerWidth, isOpen, onClose, isMobile }) {
    const theme = useTheme();

    // --- UPDATED LOGIC ---
    // 1. Consume the active chatbot state from the global context.
    const { activeChatbot } = useChatbots();

    // 2. Derive the setup completion status directly from the active chatbot.
    // If there's no active bot, setup is considered incomplete.
    const isChatbotSetupComplete = activeChatbot ? activeChatbot.is_process_completed : false;
    // --- END UPDATED LOGIC ---

    const chatbotIdentity = activeChatbot ? activeChatbot.chatbot_identity : null;


    const [openSections, setOpenSections] = useState({
        chatbot: true,
        main: false,
        config: false,
        data: false,
        connectors: false,
        monetize: false,
        sessions: false,
        improvements: false,
        payment_system: false,
    });

    const drawerStyle = {
        width: drawerWidth, // Use the prop
        flexShrink: 0,
        '& .MuiDrawer-paper': {
            width: drawerWidth, // Use the prop
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRight: 'none',
            borderBottomRightRadius: isMobile ? 0 : theme.shape.borderRadius * 2,
            borderTopRightRadius: isMobile ? 0 : theme.shape.borderRadius * 2,
            marginTop: isMobile ? 0 : theme.spacing(2),
            marginBottom: isMobile ? 0 : theme.spacing(2),
            height: isMobile ? '100%' : `calc(100% - ${theme.spacing(4)})`,
        },
    };
    
    // The logic of this function is correct and now uses the context-derived `isChatbotSetupComplete`
    const getLinkProps = (isFeatureLink = true) => {
        if (isFeatureLink && !isChatbotSetupComplete) {
            return {
                onClick: (e) => e.preventDefault(),
            };
        }
        return {
            onClick: handleDrawerToggle, // Default action
        };
    };

    const handleSectionToggle = (section) => {
        setOpenSections(prevState => ({
            ...prevState,
            [section]: !prevState[section],
        }));
    };

    const handleDrawerToggle = () => {
        // Close the drawer on link click if it's in mobile view
        if (isMobile && onClose) {
            onClose();
        }
    };

    const drawerProps = {
        sx: drawerStyle,
        variant: isMobile ? 'temporary' : 'persistent',
        anchor: 'left',
        open: isOpen, // Control open state via prop
        onClose: onClose, // Function to close it (especially on mobile)
    };

    return (
        <Drawer {...drawerProps}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', mt: isMobile ? 0 : 8 }}> {/* Margin top for Header on desktop */}
                <List component="nav" sx={{ flexGrow: 1 }}>
                    {/* Main Section - Collapsible */}

                    

                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('chatbot')}>
                            <SidebarHeaderText>Chatbots</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle chatbot">
                                {openSections.chatbot ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.chatbot} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding>
                                <StyledListItemButton
                                    component={NavLink}
                                    to={`/my-chatbot`}
                                    sx={{ pl: 4 }}
                                    {...getLinkProps(false)} // Dashboard is always enabled
                                    disabled={false}
                                >
                                    <StyledListItemIcon>
                                        <SmartToyIcon />
                                    </StyledListItemIcon>
                                    <StyledListItemText primary="My Chatbots" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('main')}>
                            <SidebarHeaderText>Statistics</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle main">
                                {openSections.main ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.main} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding>
                                <StyledListItemButton
                                    component={NavLink}
                                    to={`chatbot/${chatbotIdentity}/dashboard`}
                                    sx={{ pl: 4 }}
                                    {...getLinkProps(false)} // Dashboard is always enabled
                                    disabled={false}
                                >
                                    <StyledListItemIcon>
                                        <DashboardIcons />
                                    </StyledListItemIcon>
                                    <StyledListItemText primary="Dashboard" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>

                    {/* Configuration Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('config')}>
                            <SidebarHeaderText>Chatbot Settings</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle config">
                                {openSections.config ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.config} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding>
                                <StyledListItemButton component={NavLink}  to={`chatbot/${chatbotIdentity}/avatar`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}>
                                    <StyledListItemIcon><AccountCircleIcon /></StyledListItemIcon>
                                    <StyledListItemText primary="Avatar" />
                                </StyledListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/audio`} sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}>
                                    <StyledListItemIcon><AudioFileIcon /></StyledListItemIcon>
                                    <StyledListItemText primary="Audio & Language" />
                                </StyledListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/chatbot-settings`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}>
                                    <StyledListItemIcon><ChatIcon /></StyledListItemIcon>
                                    <StyledListItemText primary="Chatbot Settings" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>

                    {/* Data Sources Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('data')}>
                            <SidebarHeaderText>Data Sources</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle data">
                                {openSections.data ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.data} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/website`}   sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><LinkIcon /></StyledListItemIcon><StyledListItemText primary="Website" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/documents`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><DescriptionIcon /></StyledListItemIcon><StyledListItemText primary="Documents" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/images`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><ImageIcon /></StyledListItemIcon><StyledListItemText primary="Image" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/audio`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><HeadphonesIcon /></StyledListItemIcon><StyledListItemText primary="Audio" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/knowledge`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><AutoStoriesIcon /></StyledListItemIcon><StyledListItemText primary="Knowledge" /></StyledListItemButton></ListItem>
                        </List>
                    </Collapse>

                    {/* Connectors Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('connectors')}>
                            <SidebarHeaderText>Integrations</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle connectors">
                                {openSections.connectors ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.connectors} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/connectors`} sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><BoltIcon /></StyledListItemIcon><StyledListItemText primary="Connectors" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/Channels`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><ForumIcon /></StyledListItemIcon><StyledListItemText primary="Channels" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/Agents`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><SmartToyIcon /></StyledListItemIcon><StyledListItemText primary="Agents" /></StyledListItemButton></ListItem>
                        </List>
                    </Collapse>

                    {/* Marketing Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('monetize')}>
                            <SidebarHeaderText>Marketing</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle monetize">
                                {openSections.monetize ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.monetize} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding><StyledListItemButton component={NavLink}  to={`chatbot/${chatbotIdentity}/campaigns`} sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><CampaignIcon /></StyledListItemIcon><StyledListItemText primary="Campaigns" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink}  to={`chatbot/${chatbotIdentity}/advertisements`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><AttachMoneyIcon /></StyledListItemIcon><StyledListItemText primary="Advertisements" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink}  to={`chatbot/${chatbotIdentity}/marketplace-settings`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><AttachMoneyIcon /></StyledListItemIcon><StyledListItemText primary="Marketplace Settings" /></StyledListItemButton></ListItem>
                        </List>
                    </Collapse>

                    {/* Tracking Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('sessions')}>
                            <SidebarHeaderText>Tracking</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle sessions">
                                {openSections.sessions ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.sessions} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/chat-history`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><HistoryIcon /></StyledListItemIcon><StyledListItemText primary="Sessions" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/exception-logging`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><BugReportIcon /></StyledListItemIcon><StyledListItemText primary="Logging" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/feedbacks`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><FeedbackIcon /></StyledListItemIcon><StyledListItemText primary="Feedbacks" /></StyledListItemButton></ListItem>
                            {/* <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/analytics`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><SmartToyIcon /></StyledListItemIcon><StyledListItemText primary="Analytics" /></StyledListItemButton></ListItem> */}

                        </List>
                    </Collapse>

                    {/* Additional Settings Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('improvements')}>
                            <SidebarHeaderText>Additional Settings</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle improvements">
                                {openSections.improvements ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.improvements} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/question-improvements`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><QuestionMarkIcon /></StyledListItemIcon><StyledListItemText primary="Question Quality" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/answers-improvements`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><FactCheckIcon /></StyledListItemIcon><StyledListItemText primary="Ground Truth" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/pronouns`} sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><AbcIcon /></StyledListItemIcon><StyledListItemText primary="Pronouns" /></StyledListItemButton></ListItem>
                            <ListItem disablePadding>
                                <StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/configuration`}  sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}>
                                    <StyledListItemIcon><SettingsIcon /></StyledListItemIcon>
                                    <StyledListItemText primary="Website Configuration" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>

                     {/* Payment System Section - Collapsible */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('payment_system')}>
                            <SidebarHeaderText>Payments System</SidebarHeaderText>
                            <IconButton edge="end" size="small" aria-label="toggle payment_system">
                                {openSections.payment_system ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.payment_system} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding><StyledListItemButton component={NavLink} to={`chatbot/${chatbotIdentity}/payments`} sx={{ pl: 4 }} {...getLinkProps()} disabled={!isChatbotSetupComplete}><StyledListItemIcon><Payment /></StyledListItemIcon><StyledListItemText primary="Manage Payments" /></StyledListItemButton></ListItem>
                        </List>
                    </Collapse>
                </List>
                <Divider />
                <UpgradePlan/>
            </Box>
        </Drawer>
    );
}
